import React from 'react';

import { FormattedMessage, type MessageDescriptor } from 'react-intl';

import { useAnalyticsEvents } from '@atlaskit/analytics-next/useAnalyticsEvents';
import { cssMap, cx } from '@atlaskit/css';
import Image from '@atlaskit/image';
import { Box } from '@atlaskit/primitives/compiled/box';
import { Stack } from '@atlaskit/primitives/compiled/stack';
import { Text } from '@atlaskit/primitives/compiled/text';
import Radio from '@atlaskit/radio/radio';
import { token } from '@atlaskit/tokens';
import {
	progressCirclesNavigation,
	thumbnailNavigation,
} from '@atlassian/company-hub-common/assets';

import { carouselConfigAnalyticSource } from '../../../../constants';
import type { UpdateMainFormFieldType } from '../../../../controllers/use-carousel-extension-parameters-and-actions/types';
import { NavigationStyle } from '../../../../types';
import { messages } from './messages';

type NavigationOption = {
	label: MessageDescriptor;
	navigationOption: NavigationStyle;
	optionImage: string;
};

type CarouselConfigStylesSectionNavigationProps = {
	navigationType?: NavigationStyle;
	updateMainFormField: UpdateMainFormFieldType;
};

const styles = cssMap({
	border: {
		border: `${token('border.width')} solid ${token('color.border')}`,
		borderRadius: token('radius.small'),
	},
	borderSelected: {
		borderColor: token('color.border.accent.blue'),
	},
	optionContainer: {
		backgroundColor: token('color.background.neutral.subtle'),
		paddingBottom: token('space.100'),
	},
	optionImage: {
		width: '100%',
		paddingInline: token('space.100'),
		paddingTop: token('space.150'),
		paddingBottom: token('space.050'),
	},
});

const navigationOptions: NavigationOption[] = [
	{
		navigationOption: NavigationStyle.PROGRESS_CIRCLES,
		optionImage: progressCirclesNavigation,
		label: messages.progressCirclesNavigationStyleLabel,
	},
	{
		navigationOption: NavigationStyle.THUMBNAILS,
		optionImage: thumbnailNavigation,
		label: messages.thumbnailsNavigationStyleLabel,
	},
];

export const CarouselConfigStylesSectionNavigation = ({
	navigationType = NavigationStyle.PROGRESS_CIRCLES,
	updateMainFormField,
}: CarouselConfigStylesSectionNavigationProps): React.JSX.Element => {
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const handleNavigationTypeUpdate = (selectedNavigationOption: NavigationStyle) => {
		updateMainFormField({ navigationStyle: selectedNavigationOption });
		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				action: 'clicked',
				actionSubject: 'navigationStyle',
				actionSubjectId: 'configPanelNavigationStyle',
				source: carouselConfigAnalyticSource,
				attributes: {
					selectedValue: selectedNavigationOption,
				},
			},
		}).fire();
	};

	return (
		<Stack spread="space-between" alignBlock="center">
			<Stack space="space.150">
				{navigationOptions.map(({ navigationOption, optionImage, label }) => {
					return (
						<Box
							xcss={cx(
								styles.border,
								styles.optionContainer,
								navigationType === navigationOption && styles.borderSelected,
							)}
							key={navigationOption}
						>
							<Box xcss={cx(styles.optionImage)}>
								<Image src={optionImage} alt="" testId={`${navigationOption}--image`} />
							</Box>
							<Radio
								onChange={() => handleNavigationTypeUpdate(navigationOption)}
								value={navigationOption}
								testId={`${navigationOption}${navigationType === navigationOption ? '--selected' : ''}`}
								label={
									<Text size="small" align="center" weight="semibold">
										<FormattedMessage {...label} />
									</Text>
								}
								isChecked={navigationType === navigationOption}
							/>
						</Box>
					);
				})}
			</Stack>
		</Stack>
	);
};
